import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi, type BusinessSignupRequest } from '@/api/auth';
import { getApiErrorMessage } from '@/utils/apiError';

type Segment = 'individual' | 'team' | 'salon' | 'shop';

const SEGMENT_OPTIONS: { v: Segment; emoji: string; ar: string; en: string; subAr: string; subEn: string }[] = [
  { v: 'individual', emoji: '🛠️', ar: 'فني فردي', en: 'Individual',
    subAr: 'سباك / كهربائي / فني', subEn: 'Plumber / electrician / tech' },
  { v: 'team',       emoji: '🚚', ar: 'شركة بفريق', en: 'Team business',
    subAr: 'توصيل / خدمات منزلية', subEn: 'Delivery / home services' },
  { v: 'salon',      emoji: '💇', ar: 'صالون / مركز', en: 'Salon / center',
    subAr: 'حجوزات بالمواعيد', subEn: 'Appointment-based' },
  { v: 'shop',       emoji: '🛒', ar: 'متجر', en: 'Shop',
    subAr: 'بقالة / صيدلية / ملابس', subEn: 'Grocery / pharmacy / retail' },
];

/// Phase 3.R — self-serve provider signup. Lands at /admin-signup. Lets the
/// landing pages' "Start free" CTA finally do something other than redirect
/// to admin login. Pre-fills the segment from the `?segment=` query param so
/// salon visitors who clicked the salon landing don't have to pick again.
export function BusinessSignupPage() {
  const [searchParams] = useSearchParams();
  const { i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith('ar');

  const segmentFromUrl = searchParams.get('segment') as Segment | null;
  const initialSegment: Segment | null =
    segmentFromUrl && SEGMENT_OPTIONS.some((o) => o.v === segmentFromUrl) ? segmentFromUrl : null;

  const [segment, setSegment] = useState<Segment | null>(initialSegment);
  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mobile, setMobile] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  // Pre-fill segment from URL once on mount (idempotent — won't reset if user changes it).
  useEffect(() => {
    if (!segment && initialSegment) setSegment(initialSegment);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const text = {
    title: isArabic ? 'سجّل عملك في مكسب' : 'Sign up your business',
    subtitle: isArabic ? 'مجاني — تبدأ خلال دقائق' : 'Free — start in minutes',
    pickSegment: isArabic ? 'أيّ هذه يصف عملك؟' : 'Which describes your business?',
    businessName: isArabic ? 'اسم العمل' : 'Business name',
    businessNamePh: isArabic ? 'صالون سارة' : 'Sarah\'s salon',
    email: isArabic ? 'البريد الإلكتروني' : 'Email',
    emailPh: isArabic ? 'sara@example.com' : 'sara@example.com',
    password: isArabic ? 'كلمة المرور' : 'Password',
    passwordHint: isArabic ? '6 أحرف على الأقل' : '6+ characters',
    mobile: isArabic ? 'رقم الهاتف' : 'Mobile',
    mobilePh: isArabic ? '0912345678' : '0912345678',
    submit: isArabic ? 'إنشاء الحساب' : 'Create account',
    loading: isArabic ? 'جاري الإنشاء...' : 'Creating...',
    haveAccount: isArabic ? 'لديك حساب؟' : 'Already have an account?',
    signIn: isArabic ? 'تسجيل الدخول' : 'Sign in',
    needSegment: isArabic ? 'اختر نوع عملك أولاً' : 'Pick your business type first',
    checkEmailTitle: isArabic ? 'تحقق من بريدك الإلكتروني' : 'Check your email',
    checkEmailBody: isArabic
      ? 'تم إنشاء حساب العمل. أرسلنا رابط التفعيل إلى بريدك. افتح الرابط ثم سجّل الدخول لإدارة عملك.'
      : 'Your business account was created. Open the verification link sent to your mailbox, then sign in to manage your business.',
    submittedFor: isArabic ? 'أُرسل إلى' : 'Sent to',
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!segment) {
      setError(text.needSegment);
      return;
    }
    setIsLoading(true);
    try {
      const payload: BusinessSignupRequest = {
        businessName: businessName.trim(),
        email: email.trim().toLowerCase(),
        password,
        mobile: mobile.trim(),
        segment,
      };
      const res = await authApi.businessSignup(payload);
      if (!res.registrationPending || !res.emailConfirmationRequired) {
        throw new Error(isArabic ? 'استجابة تسجيل غير صالحة' : 'Invalid signup response');
      }
      setSubmittedEmail(payload.email);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, isArabic ? 'فشل إنشاء الحساب' : 'Signup failed'));
    } finally {
      setIsLoading(false);
    }
  };

  if (submittedEmail) {
    return (
      <div className="space-y-6 text-center" role="status" aria-live="polite">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-3xl">
          ✉️
        </div>
        <div>
          <h2 className="text-2xl font-bold">{text.checkEmailTitle}</h2>
          <p className="mt-3 text-sm text-muted-foreground">{text.checkEmailBody}</p>
          <p className="mt-3 text-sm font-medium" dir="ltr">
            {text.submittedFor}: {submittedEmail}
          </p>
        </div>
        <Link
          to="/admin-login"
          className="inline-flex w-full items-center justify-center rounded-lg bg-primary px-4 py-3 font-medium text-primary-foreground hover:bg-primary/90"
        >
          {text.signIn}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold">{text.title}</h2>
        <p className="mt-2 text-muted-foreground text-sm">{text.subtitle}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm text-center">
            {error}
          </div>
        )}

        {/* Segment picker — pre-filled from URL when the visitor came from a
            segment-specific landing page. */}
        <div>
          <label className="block text-sm font-medium mb-2">{text.pickSegment}</label>
          <div className="grid grid-cols-2 gap-2">
            {SEGMENT_OPTIONS.map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setSegment(opt.v)}
                className={`text-start px-3 py-3 rounded-lg border-2 transition-all ${
                  segment === opt.v
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/40'
                }`}
              >
                <div className="text-2xl mb-1">{opt.emoji}</div>
                <div className="font-bold text-sm">{isArabic ? opt.ar : opt.en}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {isArabic ? opt.subAr : opt.subEn}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="bizname" className="block text-sm font-medium">{text.businessName}</label>
          <input
            id="bizname"
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            required
            placeholder={text.businessNamePh}
            className="w-full px-4 py-3 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="email" className="block text-sm font-medium">{text.email}</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder={text.emailPh}
            dir="ltr"
            className="w-full px-4 py-3 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="mobile" className="block text-sm font-medium">{text.mobile}</label>
          <input
            id="mobile"
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            required
            placeholder={text.mobilePh}
            dir="ltr"
            className="w-full px-4 py-3 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="password" className="block text-sm font-medium">{text.password}</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            dir="ltr"
            className="w-full px-4 py-3 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <p className="text-xs text-muted-foreground">{text.passwordHint}</p>
        </div>

        <button
          type="submit"
          disabled={isLoading || !segment}
          className="w-full py-3 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          {isLoading ? text.loading : text.submit}
        </button>

        <div className="text-center text-sm text-muted-foreground">
          {text.haveAccount}{' '}
          <Link to="/admin-login" className="text-primary hover:underline font-medium">
            {text.signIn}
          </Link>
        </div>
      </form>
    </div>
  );
}
